import classNames from "classnames";
import { formatTimeString } from "../../helpers/dayjs.helper";
import { Duration } from "../../models/schedule/duration";
import { SchedulerLesson } from "../../models/schedule/lesson";
import ToggleButton from "../ui-lib/buttons/ToggleButton";
import { ContentComponent } from "../ui-lib/content-factory/ContentComponent";
import { ISchedulerViewMode } from "../ui-lib/scheduler/ISchedulerViewMode";
import { useMemo, useRef } from "react";
import useRefSizeObserver from "../../hooks/useRefSizeObserver";
import { MinusIcon, PlusIcon } from "@heroicons/react/20/solid";

type SchedulerGridProps = {
  duration: Duration[];
  lessons: SchedulerLesson[][];
  viewMode: ISchedulerViewMode<SchedulerLesson>;
  toggleActive: (day: number, duration: number) => void;
  toggleDay: (day: number, value: boolean) => void;
  isReadOnly?: boolean;
  startDate: Date;
};

const SchedulerGrid = ({
  duration,
  lessons,
  viewMode,
  toggleActive,
  toggleDay,
  startDate,
  isReadOnly = false,
}: SchedulerGridProps) => {
  const columns = lessons.length + 1;
  const rows = duration.length;
  const titles = useMemo(() => {
    return lessons.map((_, i) => viewMode.getWeekday(startDate, i).shortName);
  }, [lessons, startDate, viewMode]);

  const schedulerRef = useRef(null);
  const width = useRefSizeObserver(schedulerRef?.current);

  return (
    <div
      className="grid w-full border"
      style={{
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gridTemplateRows: `repeat(${rows + 1}, minmax(0, 1fr))`,
      }}
      ref={schedulerRef}
    >
      {/* Empty cell for the first row, first column (no title for duration) */}
      <div className="border"></div>

      {/* Titles for the lesson columns */}
      {titles.map((title, colIndex) => (
        <div
          key={`title-${colIndex}`}
          className="flex items-center justify-center border text-center font-bold"
          style={{ gridColumn: colIndex + 2, gridRow: 1 }}
        >
          <span>{title}</span>
          {!isReadOnly && lessons[colIndex].length > 0 && (
            <ToggleButton
              type="button"
              className="ml-2 h-5 w-5 !p-0"
              rounded="full"
              toggledOn={lessons[colIndex]
                .filter((l) => l.canActivate)
                .some((l) => !l.isActive)}
              disabled={lessons[colIndex].every((l) => !l.canActivate)}
              onToggle={() =>
                toggleDay(
                  lessons[colIndex][0].lessonIndex,
                  lessons[colIndex]
                    .filter((l) => l.canActivate)
                    .some((l) => !l.isActive),
                )
              }
              data-cy="scheduler-select-all-button"
            >
              {lessons[colIndex]
                .filter((l) => l.canActivate)
                .some((l) => !l.isActive) && (
                <PlusIcon className="m-auto h-3 w-3 text-white"></PlusIcon>
              )}
              {lessons[colIndex]
                .filter((l) => l.canActivate)
                .every((l) => l.isActive) && (
                <MinusIcon className="m-auto h-3 w-3 text-th-primary-900"></MinusIcon>
              )}
            </ToggleButton>
          )}
        </div>
      ))}

      {/* First column for durations (displaying vertically) */}
      {duration.map((dur, rowIndex) => (
        <div
          key={`duration-${rowIndex}`}
          className="flex items-center justify-center border"
          style={{ gridColumn: "1", gridRow: rowIndex + 2 }} // Starting from row 2
        >
          <span data-cy="scheduler-start-date">
            {formatTimeString(dur.startTime)} - {formatTimeString(dur.endTime)}
          </span>
        </div>
      ))}

      {/* Populate the rest of the grid with lessons */}
      {lessons.map((lessonColumn, colIndex) =>
        lessonColumn.map((lesson, rowIndex) => (
          <div
            key={`lesson-${colIndex}-${rowIndex}`}
            className="flex items-center justify-center border"
            style={{
              gridColumn: colIndex + 2, // +2 because column 1 is for duration
              gridRow: rowIndex + 2, // +2 because row 1 is for titles
            }}
          >
            <ToggleButton
              type="button"
              toggledOn={lesson.isActive}
              onToggle={(_) => toggleActive(lesson.lessonIndex, rowIndex)}
              disabled={!lesson.canActivate || isReadOnly}
              className={classNames(
                "relative h-full min-h-14 w-full rounded-none border-none",
                viewMode.getLessonComponentStyle(lesson),
              )}
              data-cy="scheduler-lesson-button"
              theme={viewMode.getLessonComponentColor(lesson)}
            >
              {(!isReadOnly || lesson.isActive) && (
                <ContentComponent
                  content={
                    viewMode.getLessonContent(lesson, {
                      size: width < 700 ? "small" : "large",
                    }) ?? undefined
                  }
                />
              )}
            </ToggleButton>
          </div>
        )),
      )}
    </div>
  );
};

export default SchedulerGrid;
